<template>
  <div class="notifications-list">
    <LLLoader v-if="fetcher.isFetching && !fetcher.isPaginationFetching" class="notifications-list__loader" />

    <LLLoaderError v-else-if="error" class="notifications-list__error" @retry="fetchFirst" />

    <LLEmptyState v-else-if="!items.length" class="notifications-list__empty">
      {{ $tc('common_amount_notifications', 0) }}
    </LLEmptyState>

    <div v-else class="notifications-list__list">
      <NotificationItem
        v-for="n in items"
        :key="n.id"
        :hide-actions="hideItemsActions"
        class="notifications-list__item"
        :notification="n"
        @mark-read="markOneAsRead"
        @clear="clearOne"
        @click="openOne"
        @close="$emit('close')"
        @update="onUpdateNotification"
      />

      <InfiniteScrollObserver
        v-if="!fetcher.isFetching && !fetcher.isPaginationFinished"
        root-margin="24px"
        @intersect="fetchMore"
      />
      <LLLoader v-if="fetcher.isPaginationFetching" class="notifications-list__pagination-loader" />
    </div>
  </div>
</template>

<script>
import { Fetcher } from '@/helpers/fetcher'
import LLLoader from '@/components/common/LLLoader.vue'
import LLEmptyState from '@/components/common/ui-components/LLEmptyState.vue'
import InfiniteScrollObserver from '@/components/utils/InfiniteScrollObserver.vue'
import NotificationItem from './notifications-item.vue'
import { NotificationModel } from '@/models/notification'
import LLLoaderError from '@/components/common/LLLoaderError.vue'
import Notifications, { notificationEventEnums } from '@/utils/modules/notifications'

export default {
  name: 'NotificationsList',
  components: { LLLoaderError, LLLoader, LLEmptyState, InfiniteScrollObserver, NotificationItem },
  props: {
    perPage: { type: Number, default: 20 },
    unreadOnly: { type: Boolean, default: false },
    unclearedOnly: { type: Boolean, default: false },
    hideItemsActions: { type: Boolean, default: false }
  },
  data() {
    return {
      fetcher: new Fetcher({ perPage: this.perPage }),
      lastCreatedAt: null,
      error: null
    }
  },
  computed: {
    items() {
      return this.fetcher.data
        .filter((n) => (!this.unreadOnly || !n.isRead) && (!this.unclearedOnly || !n.isCleared))
        .sort((a, b) => b.createdAt - a.createdAt)
    }
  },
  watch: {
    unreadOnly() {
      this.fetchFirst()
    }
  },
  created() {
    this.fetchFirst()
  },
  mounted() {
    this.initSocketEvents()
  },
  beforeDestroy() {
    this.destroySocketEvents()
  },
  methods: {
    initSocketEvents() {
      Notifications.addEvent(notificationEventEnums.CREATE, this.onCreateNotification)
      Notifications.addEvent(notificationEventEnums.UPDATE, this.onUpdateNotification)
      Notifications.addEvent(notificationEventEnums.DELETE, this.onDeleteNotification)
      Notifications.addEvent(notificationEventEnums.BULK_READ, this.onBulkReadNotification)
      Notifications.addEvent(notificationEventEnums.BULK_CLEAR, this.onBulkClearNotification)
    },
    destroySocketEvents() {
      Notifications.removeEvent(notificationEventEnums.CREATE, this.onCreateNotification)
      Notifications.removeEvent(notificationEventEnums.UPDATE, this.onUpdateNotification)
      Notifications.removeEvent(notificationEventEnums.DELETE, this.onDeleteNotification)
      Notifications.removeEvent(notificationEventEnums.BULK_READ, this.onBulkReadNotification)
      Notifications.removeEvent(notificationEventEnums.BULK_CLEAR, this.onBulkClearNotification)
    },
    async fetchFirst() {
      this.lastCreatedAt = null
      this.fetcher.reset()
      await this.fetchMore()
    },
    onBulkReadNotification({ notificationIdList, isRead }) {
      const notificationIdSet = new Set(notificationIdList)
      this.fetcher.data = this.fetcher.data.map((n) =>
        notificationIdSet.has(n.id) ? new NotificationModel({ ...n, isRead }) : n
      )
    },
    onBulkClearNotification({ notificationIdList, isCleared }) {
      const notificationIdSet = new Set(notificationIdList)
      this.fetcher.data = this.fetcher.data.map((n) =>
        notificationIdSet.has(n.id) ? new NotificationModel({ ...n, isCleared }) : n
      )
    },
    onUpdateNotification({ notification }) {
      this.fetcher.data = this.fetcher.data.map((n) => (n.id === notification.id ? notification : n))
    },
    onCreateNotification({ notification }) {
      this.fetcher.data = [notification, ...this.fetcher.data]
    },
    onDeleteNotification({ notificationId }) {
      this.fetcher.data = this.fetcher.data.filter((n) => n.id !== notificationId)
    },
    async fetchMore() {
      await this.fetcher.fetch(async ({ perPage }) => {
        try {
          const params = {
            unreadOnly: this.unreadOnly,
            isCleared: this.unclearedOnly ? false : undefined,
            limit: perPage
          }
          if (this.lastCreatedAt) {
            params.before = this.lastCreatedAt
          }

          const { notifications, totalRecords } = await this.$api.notifications.list(params)

          const parsed = (notifications || []).map((n) => NotificationModel.parseFromApi({ notification: n }))

          if (parsed.length) {
            const last = parsed[parsed.length - 1]
            this.lastCreatedAt = last.createdAt ? last.createdAt : null
          }

          return {
            data: parsed,
            totalRecords
          }
        } catch (e) {
          console.log(e)
          // eslint-disable-next-line no-console
          this.error = e
          this.fetcher.isPaginationFinished = true
          console.error(e)
        }
      })
    },

    async markOneAsRead(n) {
      try {
        const { notification } = await this.$api.notifications.markAsRead({ id: n.id })
        const idx = this.fetcher.data.findIndex((x) => x.id === n.id)
        if (idx !== -1) this.$set(this.fetcher.data, idx, { ...this.fetcher.data[idx], isRead: true })

        this.$emit('mark-read', notification || n)
      } catch (e) {
        console.error(e)
      }
    },

    async clearOne(n) {
      try {
        await this.$api.notifications.clear({ id: n.id })
        const idx = this.fetcher.data.findIndex((x) => x.id === n.id)
        if (idx !== -1) this.fetcher.data.splice(idx, 1)
        this.$emit('clear', n)
      } catch (e) {
        console.error(e)
      }
    },

    async markAll() {
      try {
        await this.$api.notifications.markAllAsRead()
        this.fetcher.data = this.fetcher.data.map((it) => (it.isRead ? it : { ...it, isRead: true }))
        this.$emit('mark-all-read')
      } catch (e) {
        console.error(e)
      }
    },

    async clearAll() {
      try {
        await this.$api.notifications.clearAllRead()
        this.fetcher.data = this.fetcher.data.filter((it) => !it.isRead)
        this.$emit('clear-all-read')
      } catch (e) {
        console.error(e)
      }
    },

    openOne(n) {
      this.$emit('open', n)
    }
  }
}
</script>

<style lang="scss" scoped>
.notifications-list {
  @apply bg-white flex flex-col;
  &__loader {
    @apply p-4 w-full h-full;
  }
  &__error {
    @apply w-full h-full;
  }
  &__pagination-loader {
    @apply p-3;
  }
  &__list {
    @apply flex flex-col;
  }
  &__toolbar {
    @apply border-t border-neutral-01-50 p-2 flex gap-2;
  }
  .is-active {
    @apply bg-neutral-01-100;
  }
  &__item {
    @apply mt-4;
    &:first-child {
      @apply mt-0;
    }
  }
  &__empty {
    @apply h-full;
  }
}
</style>
